/**
 * ThumbHash Utilities
 * Generates compact image placeholders for mobile app consumption
 * ThumbHash: ~28 bytes, better color accuracy, faster encode/decode than blurhash
 */

import { rgbaToThumbHash } from "thumbhash";

/**
 * Generate thumbhash from image file
 * Extracts RGBA pixel data and encodes as base64 thumbhash string
 *
 * @param file - Image file to process
 * @returns Base64 thumbhash string (~40-50 chars when base64 encoded) or empty string on error
 */
export async function generateThumbHash(file: File): Promise<string> {
  try {
    // Load image to canvas
    const img = await loadImage(file);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      console.warn(
        "Canvas 2D context not available, skipping thumbhash generation"
      );
      return "";
    }

    // Resize to consistent 100x100 for optimal balance between quality and size
    canvas.width = 100;
    canvas.height = 100;
    ctx.drawImage(img, 0, 0, 100, 100);

    // Extract RGBA pixel data
    const imageData = ctx.getImageData(0, 0, 100, 100);
    const uint8Array = imageData.data;

    // Convert to Uint8ClampedArray if needed
    const rgba = new Uint8ClampedArray(uint8Array);

    // Generate thumbhash from RGBA data (100x100 = 10000 pixels)
    // Returns Uint8Array representing the compact hash
    const hash = rgbaToThumbHash(100, 100, rgba);

    // Convert Uint8Array to base64 string for storage in Firestore
    const thumbhashString = uint8ArrayToBase64(hash);

    return thumbhashString;
  } catch (error) {
    console.warn("Failed to generate thumbhash:", error);
    // Fail gracefully - return empty string so upload continues
    return "";
  }
}

/**
 * Helper: Load image from File blob
 * @param file - Image file
 * @returns HTMLImageElement
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        resolve(img);
      };

      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };

      const dataUrl = e.target?.result;
      if (typeof dataUrl === "string") {
        img.src = dataUrl;
      } else {
        reject(new Error("Invalid file data"));
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Helper: Convert Uint8Array to base64 string
 * @param uint8Array - Uint8Array to convert
 * @returns Base64 string
 */
function uint8ArrayToBase64(uint8Array: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < uint8Array.byteLength; i++) {
    binary += String.fromCharCode(uint8Array[i]);
  }
  return btoa(binary);
}
